import axios from 'axios';
import { getApiBaseUrl } from '../config/api.js';
import { debounceApiCall } from '../utils/debounceUtils';
import { withLoadingState } from '../utils/loadingStateManager';
import { getValidTenantId } from '../utils/tenantUtils';

// Session state for managing authentication state across components
export const sessionState = {
  isAuthenticated: false,
  user: null,
  tenant: null,
  loading: true,
  setAuthentication(state, userData = null, tenantData = null) {
    this.isAuthenticated = state;
    this.user = userData;
    this.tenant = tenantData;
    this.loading = false;
  }
};

// Cache for storing recent API responses to prevent rapid duplicate requests
const apiCache = new Map();
const CACHE_TTL = 2000; // Cache time to live: 2 seconds

// Cache for storing tenant ID to avoid multiple localStorage accesses
let cachedTenantId = null;
let tenantIdCacheTime = 0;
const TENANT_CACHE_TTL = 5000; // 5 seconds

// Create axios instance with dynamic base URL
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add a SINGLE unified request interceptor to handle auth, tenant ID, and FormData
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    // If token exists, add to headers
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add tenant ID if available - use cached version if possible
    let tenantId;
    const now = Date.now();
    
    if (cachedTenantId && now - tenantIdCacheTime < TENANT_CACHE_TTL) {
      // Use cached tenant ID if it's still fresh
      tenantId = cachedTenantId;
    } else {
      // Use our optimized getValidTenantId function to get the tenant ID
      tenantId = getValidTenantId();
      
      // Update cache
      cachedTenantId = tenantId;
      tenantIdCacheTime = now;
    }
    
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }
    
    // Add user ID header if it exists
    const userId = localStorage.getItem('userId');
    if (userId) {
      config.headers['X-User-ID'] = userId;
    }
    
    // STANDARDIZED FORMDATA HANDLING:
    // For file uploads, ensure Content-Type is not set manually
    // Let axios set the proper multipart/form-data content type with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      // Log for debugging purposes
      console.log('FormData detected - Content-Type header removed for proper file upload');
      console.log('FormData contains fields:', Array.from(config.data.keys()));
    }
    
    // Don't make redundant tenant ID requests to the same endpoint
    const isTenantEndpoint = 
      config.url && (
        config.url.includes('/tenants/current') || 
        config.url.includes('/tenants?') ||
        config.url.match(/\/tenants\/[a-f0-9]{24}(\?|$)/)
      );
      
    if (isTenantEndpoint && config.headers['X-Tenant-ID']) {
      // Add flag to help debug tenant-related requests
      config._isTenantRequest = true;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a SINGLE unified response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      console.error('Authentication error:', error.response.data);
      
      // Don't auto-logout if the error is from login endpoint
      if (!error.config.url.includes('/auth/login')) {
        // Check if we should auto-logout
        const shouldLogout = !error.config.url.includes('/auth/verify-token');
        
        if (shouldLogout) {
          console.log('Session expired or invalid. Clearing auth data.');
          
          // Keep tenant data before clearing auth
          const tenantData = localStorage.getItem('tenantId');
          const currentTenant = localStorage.getItem('currentTenant');
          
          // Clear auth data
          localStorage.removeItem('token');
          localStorage.removeItem('authUser');
          localStorage.removeItem('userId');
          
          // Restore tenant data
          if (tenantData) localStorage.setItem('tenantId', tenantData);
          if (currentTenant) localStorage.setItem('currentTenant', currentTenant);
        }
      }
    }
    
    // Handle CORS errors specifically
    if (error.message && error.message.includes('Network Error')) {
      console.error('Possible CORS issue or API server is down:', error.message);
    }
    
    // Log file upload issues specifically
    if (error.config?.data instanceof FormData && error.config.url.includes('picture')) {
      console.error('Profile picture upload failed:', error.message);
      if (error.response?.data) {
        console.error('Error details:', error.response.data);
      }
    }
    
    return Promise.reject(error);
  }
);

// Original API methods
const originalApi = {
  get: api.get,
  post: api.post,
  put: api.put,
  delete: api.delete,
  patch: api.patch
};

// Override the get method to use debouncing for read operations
api.get = function(url, config = {}) {
  const cacheKey = `get:${url}:${JSON.stringify(config)}`;
  
  // Check cache first for identical recent requests
  const cached = apiCache.get(cacheKey);
  if (cached && cached.expiry > Date.now()) {
    return cached.promise;
  }
  
  // Create a debounced API call with loading state management
  const promise = withLoadingState(
    `api:${url}`,
    () => debounceApiCall(
      cacheKey,
      () => originalApi.get(url, config),
      300 // 300ms debounce delay
    )
  );
  
  // Store in cache
  apiCache.set(cacheKey, {
    promise,
    expiry: Date.now() + CACHE_TTL
  });
  
  // Clean up old cache entries periodically
  setTimeout(() => {
    apiCache.delete(cacheKey);
  }, CACHE_TTL + 1000);
  
  return promise;
};

// Export the enhanced API instance
export default api;
